// Typed client of sculpt-api's account, organization and billing routes (sculpt-api/README.md "Accounts" /
// "Organization"). One transparent refresh-and-retry on 401; every other failure is an ApiError carrying the
// API's `detail` code so pages can translate it.
import { BASE_URL, authHeader, logout as authLogout, refreshAccessToken, storeTokens } from './refreshAuth';
import type {
  ApiErrorCode,
  BillingState,
  CardOut,
  ChangeRequest,
  ChangeResult,
  CheckoutOut,
  Claim,
  ClaimKind,
  CreditGrant,
  CreditPack,
  Invitation,
  InvitationPreview,
  Invoice,
  DiscoverySource,
  Locale,
  Member,
  MemberCapChoice,
  Offer,
  Order,
  Organization,
  OrganizationPatch,
  OrganizationUsage,
  PackQuote,
  PricingContext,
  Quote,
  Role,
  SignupRequest,
  SignupResponse,
  Tokens,
  User,
  VatCheck,
} from '../types';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    public body: string,
  ) {
    super(`API ${status}: ${code}`);
  }

  is(code: ApiErrorCode): boolean {
    return this.code === code;
  }
}

/** The `detail` of a FastAPI error body, or an empty string. */
function detailOf(body: string): string {
  try {
    const parsed = JSON.parse(body) as { detail?: unknown };
    if (typeof parsed.detail === 'string') return parsed.detail;
    if (Array.isArray(parsed.detail)) return 'validation_error';
  } catch {
    /* not JSON */
  }
  return '';
}

export class ApiClient {
  private unauthorizedListeners = new Set<() => void>();

  constructor(private host: string = BASE_URL) {}

  /** Called whenever an authenticated call still answers 401 once the refresh has had its chance: the session is
   *  over, and the app goes back to /login. Returns an unsubscribe function. */
  onUnauthorized(listener: () => void): () => void {
    this.unauthorizedListeners.add(listener);
    return () => this.unauthorizedListeners.delete(listener);
  }

  /** Authenticated fetch with one refresh-and-retry on 401; a 401 that survives it notifies `onUnauthorized`. */
  private async request(path: string, init: RequestInit = {}, retried = false): Promise<Response> {
    const res = await fetch(`${this.host}${path}`, { ...init, headers: { accept: 'application/json', ...authHeader(), ...(init.headers || {}) } });
    if (res.status === 401 && !retried && (await refreshAccessToken())) return this.request(path, init, true);
    if (res.status === 401) this.unauthorizedListeners.forEach((l) => l());
    return res;
  }

  private async json<T>(res: Response): Promise<T> {
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new ApiError(res.status, detailOf(body) || `http_${res.status}`, body);
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  private body(method: string, payload?: unknown): RequestInit {
    return { method, headers: { 'Content-Type': 'application/json' }, body: payload === undefined ? undefined : JSON.stringify(payload) };
  }

  // ── public (no session) ──
  async login(email: string, password: string): Promise<Tokens> {
    const res = await fetch(`${this.host}/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', accept: 'application/json' },
      body: new URLSearchParams({ username: email, password }),
    });
    const tokens = await this.json<Tokens>(res);
    storeTokens(tokens);
    return tokens;
  }

  async signup(body: SignupRequest): Promise<SignupResponse> {
    const res = await fetch(`${this.host}/signup`, { ...this.body('POST', body), headers: { 'Content-Type': 'application/json', accept: 'application/json' } });
    const data = await this.json<SignupResponse>(res);
    storeTokens(data);
    return data;
  }

  async verifyEmail(token: string): Promise<User> {
    return this.json(await fetch(`${this.host}/signup/verify`, { ...this.body('POST', { token }), headers: { 'Content-Type': 'application/json', accept: 'application/json' } }));
  }

  async forgotPassword(email: string): Promise<void> {
    await this.json(await fetch(`${this.host}/password/forgot`, { ...this.body('POST', { email }), headers: { 'Content-Type': 'application/json', accept: 'application/json' } }));
  }

  async resetPassword(token: string, password: string): Promise<void> {
    await this.json(await fetch(`${this.host}/password/reset`, { ...this.body('POST', { token, password }), headers: { 'Content-Type': 'application/json', accept: 'application/json' } }));
  }

  /** A portal-link code from the add-in → this portal's own session (services/link.ts). */
  async exchangeLink(code: string): Promise<Tokens> {
    const res = await fetch(`${this.host}/token/exchange`, { ...this.body('POST', { code }), headers: { 'Content-Type': 'application/json', accept: 'application/json' } });
    const tokens = await this.json<Tokens>(res);
    storeTokens(tokens);
    return tokens;
  }

  /** The public catalogue (the pricing page) in `currency`, one on sale (else `currency_not_offered`): no session
   *  needed. Without it, the API's default (EUR). */
  async offers(currency?: string): Promise<Offer[]> {
    return this.json(await fetch(`${this.host}/offers${currencyQuery(currency)}`, { headers: { accept: 'application/json' } }));
  }

  async creditPacks(currency?: string): Promise<CreditPack[]> {
    return this.json(await fetch(`${this.host}/credit-packs${currencyQuery(currency)}`, { headers: { accept: 'application/json' } }));
  }

  /** The currency the pricing opens in (a guess) and the currencies on sale. */
  async pricingContext(): Promise<PricingContext> {
    return this.json(await fetch(`${this.host}/pricing/context`, { headers: { accept: 'application/json' } }));
  }

  async previewInvitation(token: string): Promise<InvitationPreview> {
    return this.json(await fetch(`${this.host}/invitations/${encodeURIComponent(token)}`, { headers: { accept: 'application/json' } }));
  }

  /** Where the Microsoft flow starts for this portal; `returnTo` is a path inside the app. */
  microsoftLoginUrl(returnTo = '/'): string {
    return `${this.apiBase()}/login/microsoft?client=portal&return_to=${encodeURIComponent(returnTo)}`;
  }

  /** "Link Microsoft" on the account page: the API links the Microsoft account only if it has `email`, the signed-in
   *  account's address, and otherwise answers `link_mismatch` (never a second account), then comes back to it. */
  microsoftLinkUrl(email: string): string {
    return `${this.microsoftLoginUrl('/')}&link_email=${encodeURIComponent(email)}`;
  }

  /** The same flow for the add-in (dialog handoff, services/handoff.ts): the API lands the tokens on the add-in's
   *  own dialog page, so nothing comes back through this portal. */
  microsoftAddinUrl(addinOrigin: string): string {
    return `${this.apiBase()}/login/microsoft?addin_origin=${encodeURIComponent(addinOrigin)}`;
  }

  private apiBase(): string {
    const base = new URL(this.host.endsWith('/') ? this.host.slice(0, -1) : this.host, window.location.origin);
    return base.toString().replace(/\/$/, '');
  }

  // ── session ──
  async me(): Promise<User> {
    return this.json(await this.request('/user'));
  }

  async logout(): Promise<void> {
    await authLogout();
  }

  async resendVerification(): Promise<{ sent: boolean }> {
    return this.json(await this.request('/signup/resend', this.body('POST')));
  }

  async changePassword(current_password: string, new_password: string): Promise<void> {
    await this.json(await this.request('/password/change', this.body('POST', { current_password, new_password })));
  }

  async patchAccount(patch: { first_name?: string; last_name?: string; locale?: Locale }): Promise<User> {
    return this.json(await this.request('/account', this.body('PATCH', patch)));
  }

  /** `source` null: the question of the first connection is skipped. */
  async answerDiscovery(source: DiscoverySource | null): Promise<User> {
    return this.json(await this.request('/account/discovery', this.body('PUT', { source })));
  }

  async acceptInvitation(token: string): Promise<User> {
    return this.json(await this.request(`/invitations/${encodeURIComponent(token)}/accept`, this.body('POST')));
  }

  // ── organization ──
  async organization(): Promise<Organization> {
    return this.json(await this.request('/organization'));
  }

  async patchOrganization(patch: OrganizationPatch): Promise<Organization> {
    return this.json(await this.request('/organization', this.body('PATCH', patch)));
  }

  async vatCheck(vat_number: string, billing_country: string, billing_zip: string): Promise<VatCheck> {
    return this.json(await this.request('/organization/vat-check', this.body('POST', { vat_number, billing_country, billing_zip: billing_zip || null })));
  }

  async members(): Promise<Member[]> {
    return this.json(await this.request('/organization/members'));
  }

  async setMemberRole(userId: number, role: 'admin' | 'member'): Promise<void> {
    await this.json(await this.request(`/organization/members/${userId}`, this.body('PATCH', { role })));
  }

  async removeMember(userId: number): Promise<void> {
    await this.json(await this.request(`/organization/members/${userId}`, this.body('DELETE')));
  }

  async transferOwnership(userId: number): Promise<void> {
    await this.json(await this.request('/organization/transfer', this.body('POST', { user_id: userId })));
  }

  async leaveOrganization(): Promise<User> {
    return this.json(await this.request('/organization/leave', this.body('POST')));
  }

  async invitations(): Promise<Invitation[]> {
    return this.json(await this.request('/organization/invitations'));
  }

  async invite(email: string, role: Exclude<Role, 'owner'>): Promise<Invitation> {
    return this.json(await this.request('/organization/invitations', this.body('POST', { email, role })));
  }

  async revokeInvitation(id: number): Promise<void> {
    await this.json(await this.request(`/organization/invitations/${id}`, this.body('DELETE')));
  }

  async claims(): Promise<Claim[]> {
    return this.json(await this.request('/organization/claims'));
  }

  async addClaim(kind: ClaimKind, value: string): Promise<Claim> {
    return this.json(await this.request('/organization/claims', this.body('POST', { kind, value })));
  }

  async setClaimAutoJoin(id: number, auto_join: boolean): Promise<Claim> {
    return this.json(await this.request(`/organization/claims/${id}`, this.body('PATCH', { auto_join })));
  }

  /** The models the organization refuses (owner/admin); the rest stay allowed. Returns the updated organization. */
  async setRefusedModels(refused: string[]): Promise<Organization> {
    return this.json(await this.request('/organization/models', this.body('PUT', { refused })));
  }

  async setReviewSample(optedOut: boolean): Promise<Organization> {
    return this.json(await this.request('/organization/review-sample', this.body('PUT', { opted_out: optedOut })));
  }

  async removeClaim(id: number): Promise<void> {
    await this.json(await this.request(`/organization/claims/${id}`, this.body('DELETE')));
  }

  async usage(): Promise<OrganizationUsage> {
    return this.json(await this.request('/organization/usage'));
  }

  // ── billing (PLAN-offers.md §8): reads for every member, writes for admins ──
  async subscription(): Promise<BillingState> {
    return this.json(await this.request('/organization/subscription'));
  }

  /** What changing to (offer, seats) would cost or when it would apply, in `currency` (the one shown; omitted, the
   *  organization's); writes nothing. */
  async previewChange(offer_key: string, seats: number, credit_tier: string | null = null, currency: string | null = null): Promise<Quote> {
    return this.json(await this.request('/organization/subscription/preview', this.body('POST', changeRequest(offer_key, seats, credit_tier, currency))));
  }

  async changeSubscription(offer_key: string, seats: number, credit_tier: string | null = null, currency: string | null = null): Promise<ChangeResult> {
    return this.json(await this.request('/organization/subscription', this.body('POST', changeRequest(offer_key, seats, credit_tier, currency))));
  }

  async cancelSubscription(): Promise<BillingState> {
    return this.json(await this.request('/organization/subscription/cancel', this.body('POST')));
  }

  /** Undo a scheduled downgrade, change of seats or cancellation. */
  async undoScheduled(): Promise<BillingState> {
    return this.json(await this.request('/organization/subscription/scheduled', this.body('DELETE')));
  }

  async credits(): Promise<CreditGrant[]> {
    return this.json(await this.request('/organization/credits'));
  }

  /** The pack's price excl. VAT and its VAT for this organization, before ordering it. */
  async previewPack(pack_key: string): Promise<PackQuote> {
    return this.json(await this.request('/organization/credits/preview', this.body('POST', { pack_key })));
  }

  async buyCredits(pack_key: string): Promise<Order> {
    return this.json(await this.request('/organization/credits', this.body('POST', { pack_key })));
  }

  async orders(): Promise<Order[]> {
    return this.json(await this.request('/organization/orders'));
  }

  /** One order, read back from Mollie first while its card payment is unsettled (the return page polls it). */
  async order(id: number): Promise<Order> {
    return this.json(await this.request(`/organization/orders/${id}`));
  }

  /** Start paying an order by card: where to send the admin (Mollie's page). */
  async checkout(id: number): Promise<CheckoutOut> {
    return this.json(await this.request(`/organization/orders/${id}/checkout`, this.body('POST')));
  }

  /** The saved card; an update under way is read back from Mollie first (the card return page polls it). */
  async card(): Promise<CardOut> {
    return this.json(await this.request('/organization/card'));
  }

  /** Save another card: a zero payment in the organization's currency on Mollie's page (`checkout_url`). */
  async updateCard(): Promise<CardOut> {
    return this.json(await this.request('/organization/card', this.body('POST')));
  }

  /** Forget the saved card (its mandate is revoked): renewals are then paid by hand. */
  async removeCard(): Promise<void> {
    await this.json(await this.request('/organization/card', this.body('DELETE')));
  }

  async cancelOrder(id: number): Promise<void> {
    await this.json(await this.request(`/organization/orders/${id}`, this.body('DELETE')));
  }

  /** An admin's limit on one member's spend per period: inherit the default, their own (in the organization's currency, a decimal string), or none. */
  async setMemberCap(userId: number, choice: MemberCapChoice): Promise<void> {
    await this.json(await this.request(`/organization/members/${userId}/cap`, this.body('PUT', choice)));
  }

  /** The limit per period every member without their own inherits (in the organization's currency, a decimal string); null removes it. */
  async setDefaultMemberCap(cap_eur: string | null): Promise<void> {
    await this.json(await this.request('/organization/member-cap-default', this.body('PUT', { cap_eur })));
  }

  async invoices(): Promise<Invoice[]> {
    return this.json(await this.request('/organization/invoices'));
  }

  /** The PDF as a blob (bearer header, so not a plain link). */
  async invoicePdf(id: number): Promise<Blob> {
    const res = await this.request(`/organization/invoices/${id}/pdf`, { headers: { accept: 'application/pdf' } });
    if (!res.ok) throw new ApiError(res.status, detailOf(await res.text().catch(() => '')) || `http_${res.status}`, '');
    return res.blob();
  }
}

function currencyQuery(currency: string | undefined): string {
  return currency ? `?currency=${encodeURIComponent(currency)}` : '';
}

function changeRequest(offer_key: string, seats: number, credit_tier: string | null, currency: string | null): ChangeRequest {
  return currency ? { offer_key, seats, credit_tier, currency } : { offer_key, seats, credit_tier };
}

export const api = new ApiClient();
